
import BaseBlock from '@headlines/utils/BaseBlock';
import BaseButton from '@core/components/UI/BaseButton.vue';
import BlockTypeSettings from '@headlines/types/block/111-120/blockType117';
import { faThumbtack } from '@delfi.lt/fa-kit/icons/modules/classic/solid';
import Service from '@root/common/base/Service';
import UniversalHeadlinesService from '@headlines/services/UniversalHeadlines.service';
import getHeadlineWithHref from '@root/modules/headlines/utils/getHeadlineWithHref';
import { DataWithHref } from '@root/modules/headlines/types/headlines';
import BlockType102 from '@headlines/components/block/100-110/block-type-102/BlockType102.vue';

type LiveblogBlockConfigItem = BlockTypeSettings['config'][number];

interface TickarooMessage {
  text: string;
  highlight?: boolean | string;
  created_at: number;
  publish_at_string: string;
  updated_at?: number;
  updated_at_string?: string;
  is_sticky?: boolean;
  sort: string | number;
  image?: string;
  is_old?: boolean;
}

const defaultSettings = {
  config: [
    {
      tickarooId: '',
      articleId: '',
      title: '',
      titleUrl: '',
      headlineTemplate: false,
      hidePublishTime: false,
      background: '',
    },
  ],
};

export default BaseBlock<BlockTypeSettings>(defaultSettings).extend({
  components: {
    BlockType102,
    BaseButton,
  },

  computed: {
    headlineLiveBlogConfig(): LiveblogBlockConfigItem | undefined {
      return this.data?.config?.find((item: LiveblogBlockConfigItem) => item.headlineTemplate === true);
    },
    hasLightPinkBackground(): boolean {
      return this.headlineLiveBlogConfig?.background?.toUpperCase() === '#FDDFDC';
    },
  },

  data() {
    return {
      activeTabIndex: 0,
      messages: {} as Record<string, TickarooMessage[]>,
      headlineItems: [] as DataWithHref[],
      faThumbtack,
    };
  },

  mounted() {
    this.fetchMessages();
    this.fetchHeadlineItems();
  },

  methods: {
    setActiveTab(index: number | string): void {
      this.activeTabIndex = Number(index);
    },

    getArticleUrl(articleId: string): string {
      return `https://www.delfi.lt/a/${articleId}`;
    },

    getMessageFadeStyle(background: string): Record<string, string> {
      return {
        background: `linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, ${background}9c 48.08%, ${background} 100%)`,
      };
    },

    getMessagePublishAtString(publishAtString: string): string {
      return publishAtString.match(/<strong\b[^>]*>[\s\S]*?<\/strong>/i)?.[0] || publishAtString;
    },

    getMessageText(text: string): string {
      const strongMatch = text.match(/<strong\b[^>]*>([\s\S]*)<\/strong>/i);

      if (strongMatch?.[1]) {
        return strongMatch[1]
          .replace(/<\/?strong\b[^>]*>/gi, '')
          .replace(/<br\s*\/?>/gi, '')
          .trim();
      }

      const plainText = text
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      return plainText.slice(0, 100);
    },

    async fetchHeadlineItems(): Promise<void> {
      if (!this.headlineLiveBlogConfig?.articleId) {
        return;
      }

      const articleId = Number(this.headlineLiveBlogConfig.articleId);

      if (Number.isNaN(articleId)) {
        return;
      }

      try {
        const universalHeadlinesService = new UniversalHeadlinesService();
        const headlines = await universalHeadlinesService.fetch({
          id: [articleId],
          limit: 1,
        });

        this.headlineItems = headlines.items.map((headline) =>
          getHeadlineWithHref({
            headline,
            domain: this.domain,
            fakeDomainEnabled: this.fakeDomainEnabled,
          })
        );
      } catch (error) {
        this.$sentry.captureException(error, {
          contexts: { vue: { componentName: 'BlockType117', method: 'fetchHeadlineItems' } },
          tags: { 'process.type': process.client ? 'client' : 'server' },
        });
      }
    },

    async fetchMessages(): Promise<void> {
      if (!this.data?.config?.length) {
        return;
      }

      const ids = this.data.config.map((item: LiveblogBlockConfigItem) => item.tickarooId);
      const service = new Service();
      const apiProvider = service.createProvider('HTTP');
      let apiUrl = '/api/tickaroo-api';

      // Needed for developing, testing and admin preview
      const referer = window.location.href.toLowerCase();
      if (referer.includes('localhost')) {
        apiUrl = 'https://localhost:3000/api/tickaroo-api';
      } else if (referer.includes('admin.test.delfi.lt')) {
        apiUrl = 'https://www.test.delfi.lt/api/tickaroo-api';
      } else if (referer.includes('admin.delfi.lt')) {
        apiUrl = 'https://www.delfi.lt/api/tickaroo-api';
      }

      try {
        const response = await apiProvider.request<
          {
            dlb_id: string;
            messages: TickarooMessage[];
          }[]
        >({
          url: apiUrl,
          method: 'post',
          data: { ids },
        });

        const mapped: Record<string, TickarooMessage[]> = {};

        response.forEach((item) => {
          mapped[item.dlb_id] = item.messages || [];
        });

        this.messages = mapped;
      } catch (error) {
        this.$sentry.captureException(error, {
          contexts: { vue: { componentName: 'BlockType117', method: 'fetchMessages' } },
          tags: { 'process.type': process.client ? 'client' : 'server' },
        });
      }
    },
  },
});
